import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  Divider,
  Stack,
  Autocomplete,
  FormControlLabel,
  Switch,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Preview as PreviewIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
  AttachMoney as MoneyIcon,
  Folder as FolderIcon,
  Assignment as ProjectIcon,
  Timeline as TimelineIcon,
  Group as TeamIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Build as BuildIcon,
  Description as DescriptionIcon,
  Code as CodeIcon,
  Assignment as TaskIcon,
  Help as HelpIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLoading } from '../../../contexts/LoadingContext';
import { useNotify } from '../../ui/NotificationSystem';
import { specKitDockerService } from '../../../services/SpecKitDockerService';
import { DiscoveryInterviewChat } from '../discovery';
import {
  EnhancedProjectFormData,
  ProjectSDDState,
  SDDPhase,
  PhaseStatus,
  ConstitutionCheckResult,
  SpecificationData,
  PlanData,
  TasksData,
  SpecKitIntegration
} from '../../../types/SDDTypes';

// Interface for assignable users response
interface AssignableUsersResponse {
  clients: Array<{ id: number; name: string; email: string; }>;
  projectManagers: Array<{ id: number; name: string; email: string; }>;
  developers: Array<{ id: number; name: string; email: string; }>;
}

const PROJECT_TYPES = [
  'Web Development',
  'Mobile App Development',
  'E-commerce Platform',
  'Custom Software',
  'API Development',
  'Database Design',
  'UI/UX Design',
  'Digital Marketing',
  'Consultation',
  'Maintenance & Support'
];

const PRIORITY_LEVELS = ['Low', 'Medium', 'High', 'Critical'];

const SKILLS = [
  'React', 'Vue.js', 'Angular', 'Node.js', 'Python', 'PHP', 'Laravel',
  'React Native', 'Flutter', 'iOS', 'Android', 'DevOps', 'AWS', 'Docker',
  'PostgreSQL', 'MongoDB', 'MySQL', 'UI/UX Design', 'Figma', 'Adobe XD'
];

const sddSteps = [
  'Project Configuration',
  'Constitution Review',
  'Discovery Interview',
  'Feature Specification',
  'Implementation Planning',
  'Task Breakdown',
  'Review & Create'
];

const CreateProjectPageEnhanced: React.FC = () => {
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const { success: showSuccess, error: showError, info: showInfo } = useNotify();
  
  const [activeStep, setActiveStep] = useState(0);
  const [assignableUsers, setAssignableUsers] = useState<AssignableUsersResponse | null>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [discoveryInterviewCompleted, setDiscoveryInterviewCompleted] = useState(false);
  const [projectIdForInterview, setProjectIdForInterview] = useState<number | null>(null);
  
  const [formData, setFormData] = useState<EnhancedProjectFormData>({
    // Basic Information
    name: '',
    description: '',
    clientId: '',
    projectType: '',
    priority: 'Medium',
    
    // Timeline & Budget
    startDate: '',
    estimatedEndDate: '',
    budget: 0,
    billableHours: true,
    hourlyRate: 75,
    
    // Team & Resources (enhanced with real IDs)
    projectManagerId: '',
    teamMemberIds: [],
    requiredSkills: [],
    
    // Project Settings
    isActive: true,
    visibility: 'team',
    notificationsEnabled: true,
    
    // SDD Workflow Configuration
    enableSDDWorkflow: true,
    enableSpecKitIntegration: true,
    clientGuidedWorkflow: true,
    requireClientApproval: true,
    
    // Initial SDD State
    sddState: {
      constitution: {
        phase: SDDPhase.CONSTITUTION,
        status: PhaseStatus.NOT_STARTED,
        checks: {
          codeQualityFirst: false,
          testDrivenDevelopment: false,
          userExperienceConsistency: false,
          performanceRequirements: false,
          specDrivenDevelopment: false
        },
        violations: []
      },
      specification: {
        phase: SDDPhase.SPECIFY,
        status: PhaseStatus.NOT_STARTED,
        title: '',
        description: '',
        userStories: [],
        functionalRequirements: [],
        successCriteria: [],
        keyEntities: [],
        assumptions: [],
        constraints: [],
        outOfScope: [],
        clarificationsNeeded: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      plan: {
        phase: SDDPhase.PLAN,
        status: PhaseStatus.NOT_STARTED,
        technicalContext: {
          technologies: [],
          dependencies: [],
          integrations: [],
          performanceTargets: [],
          securityRequirements: []
        },
        constitutionCheck: {
          phase: SDDPhase.CONSTITUTION,
          status: PhaseStatus.NOT_STARTED,
          checks: {
            codeQualityFirst: false,
            testDrivenDevelopment: false,
            userExperienceConsistency: false,
            performanceRequirements: false,
            specDrivenDevelopment: false
          },
          violations: []
        },
        researchFindings: [],
        dataModel: {
          entities: [],
          relationships: [],
          validationRules: [],
          stateTransitions: []
        },
        apiContracts: [],
        quickstart: {
          setupSteps: [],
          examples: [],
          troubleshooting: []
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      tasks: {
        phase: SDDPhase.TASKS,
        status: PhaseStatus.NOT_STARTED,
        taskBreakdown: [],
        estimations: [],
        dependencies: [],
        timeline: {
          phases: [],
          milestones: [],
          criticalPath: [],
          totalDuration: 0
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      currentPhase: SDDPhase.CONSTITUTION,
      overallStatus: PhaseStatus.NOT_STARTED,
      clientApprovalRequired: true,
      clientApprovalReceived: false,
      readyForImplementation: false,
      specKitIntegration: {
        enabled: true,
        commands: [],
        generatedFiles: []
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
  });

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoadingData(true);
        
        // Load assignable users (includes clients, PMs, and developers)
        const usersResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1'}/admin/projects/assignable-users`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }).then(response => response.json());
        
        setAssignableUsers(usersResponse.data);
        
        showInfo('Project creation data loaded successfully');
      } catch (error) {
        console.error('Error loading initial data:', error);
        showError('Failed to load project creation data');
      } finally {
        setLoadingData(false);
      }
    };

    loadInitialData();
  }, [showError, showInfo]);

  const handleInputChange = (field: keyof EnhancedProjectFormData) => (event: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleNext = () => {
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const validateCurrentStep = (): boolean => {
    switch (activeStep) {
      case 0: // Project Configuration
        return !!(formData.name && formData.clientId && formData.projectType);
      case 1: // Constitution Review
        return formData.sddState.constitution.status === PhaseStatus.APPROVED;
      case 2: // Discovery Interview
        return discoveryInterviewCompleted || !projectIdForInterview; // Can skip if no draft saved
      case 3: // Feature Specification
        return formData.sddState.specification.status === PhaseStatus.COMPLETED;
      case 4: // Implementation Planning
        return formData.sddState.plan.status === PhaseStatus.COMPLETED;
      case 5: // Task Breakdown
        return formData.sddState.tasks.status === PhaseStatus.COMPLETED;
      default:
        return true;
    }
  };

  const handleConstitutionCheck = async () => {
    try {
      setLoading('constitutionCheck', true);
      showInfo('Running Constitution compliance check...');
      
      // Use SpecKit Docker service to validate constitution compliance
      const validationResult = await specKitDockerService.validateConstitution({
        name: formData.name,
        description: formData.description,
        requirements: [formData.projectType, formData.priority],
      });
      
      const constitutionResult: ConstitutionCheckResult = {
        phase: SDDPhase.CONSTITUTION,
        status: validationResult.compliant ? PhaseStatus.APPROVED : PhaseStatus.NEEDS_CLARIFICATION,
        checks: {
          codeQualityFirst: validationResult.score >= 80,
          testDrivenDevelopment: validationResult.score >= 80,
          userExperienceConsistency: validationResult.score >= 80,
          performanceRequirements: validationResult.score >= 80,
          specDrivenDevelopment: validationResult.score >= 80
        },
        violations: validationResult.violations.map(v => ({
          principle: v.principle,
          description: v.description,
          severity: v.severity,
          justification: v.recommendation
        })),
        approvedAt: validationResult.compliant ? new Date().toISOString() : undefined,
        approvedBy: validationResult.compliant ? 'SpecKit AI System' : undefined
      };
      
      setFormData(prev => ({
        ...prev,
        sddState: {
          ...prev.sddState,
          constitution: constitutionResult,
          currentPhase: validationResult.compliant ? SDDPhase.SPECIFY : SDDPhase.CONSTITUTION
        }
      }));
      
      if (validationResult.compliant) {
        showSuccess(`Constitution check passed! Score: ${validationResult.score}/100. Ready for specification phase.`);
      } else {
        showError(`Constitution violations found. Score: ${validationResult.score}/100. Please address violations before proceeding.`);
      }
    } catch (error) {
      console.error('Constitution check failed:', error);
      showError('Constitution check failed - using fallback validation');
      
      // Fallback to simple validation
      const constitutionResult: ConstitutionCheckResult = {
        phase: SDDPhase.CONSTITUTION,
        status: PhaseStatus.APPROVED,
        checks: {
          codeQualityFirst: true,
          testDrivenDevelopment: true,
          userExperienceConsistency: true,
          performanceRequirements: true,
          specDrivenDevelopment: true
        },
        violations: [],
        approvedAt: new Date().toISOString(),
        approvedBy: 'System (Fallback)'
      };
      
      setFormData(prev => ({
        ...prev,
        sddState: {
          ...prev.sddState,
          constitution: constitutionResult,
          currentPhase: SDDPhase.SPECIFY
        }
      }));
      
      showSuccess('Constitution check passed (fallback mode)! Ready for specification phase.');
    } finally {
      setLoading('constitutionCheck', false);
    }
  };

  const handleSpecifyFeature = async () => {
    try {
      setLoading('specifyFeature', true);
      showInfo('Generating feature specification with SpecKit Docker service...');
      
      // Use SpecKit Docker service to generate specification
      const specifyResult = await specKitDockerService.specifyFeature({
        featureDescription: `${formData.name}: ${formData.description}`,
        context: `Project Type: ${formData.projectType}, Priority: ${formData.priority}`
      });
      
      setFormData(prev => ({
        ...prev,
        sddState: {
          ...prev.sddState,
          specification: {
            ...prev.sddState.specification,
            ...specifyResult.specification,
            // Convert keyEntities from strings to KeyEntity objects and ensure proper types
            keyEntities: specifyResult.specification.keyEntities.map((name: string, index: number) => ({
              id: (index + 1).toString(),
              name,
              description: `${name} entity for the project`,
              attributes: [],
              relationships: []
            })),
            status: PhaseStatus.COMPLETED,
            specFilePath: specifyResult.specFilePath
          },
          currentPhase: SDDPhase.PLAN,
          specKitIntegration: {
            ...prev.sddState.specKitIntegration,
            specFilePath: specifyResult.specFilePath,
            commands: [
              ...prev.sddState.specKitIntegration.commands,
              {
                command: 'speckit.specify',
                phase: SDDPhase.SPECIFY,
                executed: true,
                executedAt: new Date().toISOString(),
                output: `Generated specification at ${specifyResult.specFilePath}`
              }
            ],
            generatedFiles: [
              ...prev.sddState.specKitIntegration.generatedFiles,
              {
                path: specifyResult.specFilePath,
                type: 'spec' as const,
                generatedBy: 'speckit.specify',
                generatedAt: new Date().toISOString()
              }
            ]
          }
        }
      }));
      
      if (specifyResult.clarificationsNeeded.length > 0) {
        showInfo(`Specification completed with ${specifyResult.clarificationsNeeded.length} clarifications needed`);
      } else {
        showSuccess('Feature specification completed! Ready for planning phase.');
      }
    } catch (error) {
      console.error('Feature specification failed:', error);
      showError('Feature specification failed - using fallback generation');
      
      // Fallback to mock data generation
      const specificationData: Partial<SpecificationData> = {
        status: PhaseStatus.COMPLETED,
        title: formData.name,
        description: formData.description,
        userStories: [
          {
            id: '1',
            title: 'Basic Project Setup',
            description: 'As a client, I want to see my project initialized with the chosen specifications.',
            actor: 'Client',
            action: 'View project',
            outcome: 'See project structure and initial setup',
            acceptanceCriteria: ['Project structure created', 'Initial documentation available'],
            priority: 'high'
          }
        ],
        functionalRequirements: [
          {
            id: '1',
            title: 'Project Initialization',
            description: 'System must create project structure according to SDD principles',
            category: 'Core',
            testable: true,
            businessValue: 'Ensures consistent project setup',
            dependencies: [],
            risks: []
          }
        ],
        successCriteria: [
          {
            id: '1',
            metric: 'Project setup time',
            target: 'Under 5 minutes',
            measurable: true,
            technologyAgnostic: true,
            userFocused: true
          }
        ],
        keyEntities: [
          {
            id: '1',
            name: 'Project',
            description: 'Main project entity',
            attributes: [],
            relationships: []
          }
        ],
        assumptions: [],
        constraints: [],
        outOfScope: [],
        clarificationsNeeded: [],
        updatedAt: new Date().toISOString()
      };
      
      setFormData(prev => ({
        ...prev,
        sddState: {
          ...prev.sddState,
          specification: {
            ...prev.sddState.specification,
            ...specificationData
          },
          currentPhase: SDDPhase.PLAN
        }
      }));
      
      showSuccess('Feature specification completed (fallback mode)! Ready for planning phase.');
    } finally {
      setLoading('specifyFeature', false);
    }
  };

  const handleGeneratePlan = async () => {
    try {
      setLoading('generatePlan', true);
      showInfo('Generating implementation plan with SpecKit Docker service...');
      
      // Use SpecKit Docker service to generate implementation plan
      const planResult = await specKitDockerService.generatePlan({
        specificationData: formData.sddState.specification,
        technicalPreferences: {
          frontend: 'React',
          backend: 'Laravel',
          database: 'MySQL'
        }
      });
      
      setFormData(prev => ({
        ...prev,
        sddState: {
          ...prev.sddState,
          plan: {
            ...prev.sddState.plan,
            ...planResult.plan,
            status: PhaseStatus.COMPLETED,
            planFilePath: planResult.planFilePath
          },
          currentPhase: SDDPhase.TASKS,
          specKitIntegration: {
            ...prev.sddState.specKitIntegration,
            planFilePath: planResult.planFilePath,
            commands: [
              ...prev.sddState.specKitIntegration.commands,
              {
                command: 'speckit.plan',
                phase: SDDPhase.PLAN,
                executed: true,
                executedAt: new Date().toISOString(),
                output: `Generated plan at ${planResult.planFilePath}`
              }
            ],
            generatedFiles: [
              ...prev.sddState.specKitIntegration.generatedFiles,
              ...planResult.generatedFiles.map((path: string) => ({
                path,
                type: 'plan' as const,
                generatedBy: 'speckit.plan',
                generatedAt: new Date().toISOString()
              }))
            ]
          }
        }
      }));
      
      showSuccess('Implementation plan generated! Ready for task breakdown.');
    } catch (error) {
      console.error('Plan generation failed:', error);
      showError('Plan generation failed - using fallback generation');
      
      // Fallback to simple plan generation
      setFormData(prev => ({
        ...prev,
        sddState: {
          ...prev.sddState,
          plan: {
            ...prev.sddState.plan,
            status: PhaseStatus.COMPLETED,
            updatedAt: new Date().toISOString()
          },
          currentPhase: SDDPhase.TASKS
        }
      }));
      
      showSuccess('Implementation plan generated (fallback mode)! Ready for task breakdown.');
    } finally {
      setLoading('generatePlan', false);
    }
  };

  const handleGenerateTasks = async () => {
    try {
      setLoading('generateTasks', true);
      showInfo('Breaking down development tasks with SpecKit Docker service...');
      
      // Use SpecKit Docker service to generate task breakdown
      const tasksResult = await specKitDockerService.generateTasks({
        planData: formData.sddState.plan,
        teamSize: formData.teamMemberIds.length || 3,
        complexity: 'medium'
      });
      
      setFormData(prev => ({
        ...prev,
        sddState: {
          ...prev.sddState,
          tasks: {
            ...prev.sddState.tasks,
            ...tasksResult.tasks,
            status: PhaseStatus.COMPLETED
          },
          readyForImplementation: true,
          specKitIntegration: {
            ...prev.sddState.specKitIntegration,
            commands: [
              ...prev.sddState.specKitIntegration.commands,
              {
                command: 'speckit.tasks',
                phase: SDDPhase.TASKS,
                executed: true,
                executedAt: new Date().toISOString(),
                output: `Generated ${tasksResult.tasks.taskBreakdown.length} task groups`
              }
            ],
            generatedFiles: [
              ...prev.sddState.specKitIntegration.generatedFiles,
              ...tasksResult.generatedFiles.map((path: string) => ({
                path,
                type: 'documentation' as const,
                generatedBy: 'speckit.tasks',
                generatedAt: new Date().toISOString()
              }))
            ]
          }
        }
      }));
      
      showSuccess('Task breakdown completed! Project ready for implementation.');
    } catch (error) {
      console.error('Task generation failed:', error);
      showError('Task generation failed - using fallback generation');
      
      // Fallback to simple task generation
      setFormData(prev => ({
        ...prev,
        sddState: {
          ...prev.sddState,
          tasks: {
            ...prev.sddState.tasks,
            status: PhaseStatus.COMPLETED,
            updatedAt: new Date().toISOString()
          },
          readyForImplementation: true
        }
      }));
      
      showSuccess('Task breakdown completed (fallback mode)! Project ready for implementation.');
    } finally {
      setLoading('generateTasks', false);
    }
  };

  const handleSaveDraft = async () => {
    try {
      setLoading('saveDraft', true);
      showInfo('Saving project draft...');

      // Validate minimum required fields for draft
      if (!formData.name) {
        showError('Project name is required to save draft');
        return;
      }

      const clientIdNum = formData.clientId ? parseInt(formData.clientId.toString().replace(/\D/g, '')) : null;

      const requestData = {
        name: formData.name,
        description: formData.description || '',
        client_id: clientIdNum,
        project_type: formData.projectType || 'Other',
        priority: formData.priority.toLowerCase(),
        project_manager_id: formData.projectManagerId ? parseInt(formData.projectManagerId) : null,
        team_member_ids: formData.teamMemberIds.map(id => parseInt(id)),
        start_date: formData.startDate || null,
        estimated_end_date: formData.estimatedEndDate || null,
        budget: formData.budget,
        hourly_rate: formData.hourlyRate,
        billable_hours: formData.billableHours,
        visibility: formData.visibility,
        enable_sdd_workflow: formData.enableSDDWorkflow,
        enable_speckit_integration: formData.enableSpecKitIntegration,
        client_guided_workflow: formData.clientGuidedWorkflow,
        require_client_approval: formData.requireClientApproval,
        sdd_state: formData.sddState,
        status: 'draft', // Mark as draft
      };

      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1'}/admin/projects/sdd`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save draft');
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to save draft');
      }
      
      // Store the project ID for discovery interview
      const projectId = result.data.id;
      setProjectIdForInterview(projectId);
      
      showSuccess(`Draft saved successfully! Project ID: ${projectId}. You can now proceed to the discovery interview.`);
    } catch (error: any) {
      console.error('Error saving draft:', error);
      showError(error.message || 'Failed to save draft');
    } finally {
      setLoading('saveDraft', false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading('createProject', true);
      showInfo('Creating project with SDD workflow...');
      
      // Validate all required fields
      if (!formData.name || !formData.clientId || !formData.projectType || !formData.startDate || !formData.estimatedEndDate) {
        showError('Please fill in all required fields (name, client, project type, start date, end date)');
        return;
      }

      // Validate that clientId is a valid number
      // Handle both numeric strings and strings with prefixes like "client_2"
      console.log('Client ID from form:', formData.clientId, 'Type:', typeof formData.clientId);
      const clientIdStr = formData.clientId.toString().replace(/\D/g, ''); // Remove non-digits
      const clientIdNum = parseInt(clientIdStr);
      console.log('Cleaned client ID string:', clientIdStr, 'Parsed:', clientIdNum, 'isNaN:', isNaN(clientIdNum));
      if (isNaN(clientIdNum) || clientIdNum <= 0) {
        showError(`Please select a valid client (received: "${formData.clientId}")`);
        return;
      }

      if (formData.enableSDDWorkflow && !formData.sddState.readyForImplementation) {
        showError('Complete all SDD phases before creating project');
        return;
      }

      // Prepare the request data for the SDD endpoint
      const requestData = {
        name: formData.name,
        description: formData.description,
        client_id: clientIdNum,
        project_type: formData.projectType,
        priority: formData.priority.toLowerCase(),
        project_manager_id: formData.projectManagerId ? parseInt(formData.projectManagerId) : null,
        team_member_ids: formData.teamMemberIds.map(id => parseInt(id)),
        start_date: formData.startDate,
        estimated_end_date: formData.estimatedEndDate,
        budget: formData.budget,
        hourly_rate: formData.hourlyRate,
        billable_hours: formData.billableHours,
        visibility: formData.visibility,
        enable_sdd_workflow: formData.enableSDDWorkflow,
        enable_speckit_integration: formData.enableSpecKitIntegration,
        client_guided_workflow: formData.clientGuidedWorkflow,
        require_client_approval: formData.requireClientApproval,
        sdd_state: formData.sddState,
      };

      // Call the SDD project creation endpoint
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1'}/admin/projects/sdd`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create project');
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to create project');
      }
      
      showSuccess('Project created successfully with SDD workflow!');
      navigate('/dashboard/admin/projects');
    } catch (error: any) {
      console.error('Error creating project:', error);
      showError(error.message || 'Failed to create project');
    } finally {
      setLoading('createProject', false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0: // Project Configuration
        return (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                Configure your project with SDD (Spec-Driven Development) workflow enabled. 
                This ensures Constitution compliance and proper specification-driven development.
              </Alert>
              
              {!projectIdForInterview && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    💡 Want to use the Discovery Interview feature?
                  </Typography>
                  <Typography variant="body2">
                    Click "Save Draft" below after entering the project name. This will create a draft project 
                    that you can use in Step 3 (Discovery Interview) to have AI-guided conversations about your project requirements.
                  </Typography>
                </Alert>
              )}
              
              {projectIdForInterview && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  <Typography variant="subtitle2">
                    ✓ Draft Saved (Project ID: {projectIdForInterview})
                  </Typography>
                  <Typography variant="body2">
                    You can now proceed to the Discovery Interview step.
                  </Typography>
                </Alert>
              )}
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Project Name"
                value={formData.name}
                onChange={handleInputChange('name')}
                required
                placeholder="Enter project name"
              />
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Project Description"
                value={formData.description}
                onChange={handleInputChange('description')}
                placeholder="Describe the project goals and scope"
              />
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>Client User</InputLabel>
                <Select
                  value={formData.clientId}
                  onChange={handleInputChange('clientId')}
                  label="Client User"
                  disabled={!assignableUsers}
                >
                  {assignableUsers?.clients.map(client => (
                    <MenuItem key={client.id} value={client.id.toString()}>
                      {client.name} ({client.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>Project Type</InputLabel>
                <Select
                  value={formData.projectType}
                  onChange={handleInputChange('projectType')}
                  label="Project Type"
                >
                  {PROJECT_TYPES.map(type => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  onChange={handleInputChange('priority')}
                  label="Priority"
                >
                  {PRIORITY_LEVELS.map(priority => (
                    <MenuItem key={priority} value={priority}>
                      {priority}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Project Manager</InputLabel>
                <Select
                  value={formData.projectManagerId}
                  onChange={handleInputChange('projectManagerId')}
                  label="Project Manager"
                  disabled={!assignableUsers}
                >
                  {assignableUsers?.projectManagers.map(pm => (
                    <MenuItem key={pm.id} value={pm.id.toString()}>
                      {pm.name} ({pm.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                <ScheduleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Timeline & Budget
              </Typography>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={formData.startDate}
                onChange={handleInputChange('startDate')}
                required
                InputLabelProps={{ shrink: true }}
                helperText="When the project is expected to begin"
              />
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="date"
                label="Estimated End Date"
                value={formData.estimatedEndDate}
                onChange={handleInputChange('estimatedEndDate')}
                required
                InputLabelProps={{ shrink: true }}
                helperText="Expected project completion date"
              />
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Budget"
                value={formData.budget}
                onChange={handleInputChange('budget')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                helperText="Total project budget"
              />
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Hourly Rate"
                value={formData.hourlyRate}
                onChange={handleInputChange('hourlyRate')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                helperText="Billable hourly rate"
              />
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                SDD Workflow Configuration
              </Typography>
              
              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.enableSDDWorkflow}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        enableSDDWorkflow: e.target.checked 
                      }))}
                    />
                  }
                  label="Enable Spec-Driven Development Workflow"
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.enableSpecKitIntegration}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        enableSpecKitIntegration: e.target.checked 
                      }))}
                      disabled={!formData.enableSDDWorkflow}
                    />
                  }
                  label="Enable spec-kit Integration for Auto-generation"
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.clientGuidedWorkflow}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        clientGuidedWorkflow: e.target.checked 
                      }))}
                      disabled={!formData.enableSDDWorkflow}
                    />
                  }
                  label="Guide Client Through Constitution & Specification"
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.requireClientApproval}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        requireClientApproval: e.target.checked 
                      }))}
                      disabled={!formData.enableSDDWorkflow}
                    />
                  }
                  label="Require Client Approval Before Implementation"
                />
              </Stack>
            </Grid>
          </Grid>
        );

      case 1: // Constitution Review
        return (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  VegaDigital Constitution Compliance Check
                </Typography>
                All projects must comply with our core development principles before proceeding.
              </Alert>
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  <BuildIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Core Principles
                </Typography>
                
                <List>
                  <ListItem>
                    <ListItemText
                      primary="I. Code Quality First"
                      secondary="Enterprise-grade quality standards with proper error handling and documentation"
                    />
                    <CheckCircleIcon 
                      color={formData.sddState.constitution.checks.codeQualityFirst ? 'success' : 'disabled'} 
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemText
                      primary="II. Test-Driven Development (NON-NEGOTIABLE)"
                      secondary="TDD cycle strictly enforced with 90%+ test coverage"
                    />
                    <CheckCircleIcon 
                      color={formData.sddState.constitution.checks.testDrivenDevelopment ? 'success' : 'disabled'} 
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemText
                      primary="III. User Experience Consistency"
                      secondary="Design system compliance and WCAG 2.1 AA accessibility"
                    />
                    <CheckCircleIcon 
                      color={formData.sddState.constitution.checks.userExperienceConsistency ? 'success' : 'disabled'} 
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemText
                      primary="IV. Performance Requirements"
                      secondary="<2s page loads, <500ms API responses, real user monitoring"
                    />
                    <CheckCircleIcon 
                      color={formData.sddState.constitution.checks.performanceRequirements ? 'success' : 'disabled'} 
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemText
                      primary="V. Spec-Driven Development"
                      secondary="Complete specification before implementation begins"
                    />
                    <CheckCircleIcon 
                      color={formData.sddState.constitution.checks.specDrivenDevelopment ? 'success' : 'disabled'} 
                    />
                  </ListItem>
                </List>
                
                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="contained"
                    onClick={handleConstitutionCheck}
                    disabled={formData.sddState.constitution.status === PhaseStatus.APPROVED}
                    startIcon={<CheckCircleIcon />}
                  >
                    {formData.sddState.constitution.status === PhaseStatus.APPROVED 
                      ? 'Constitution Approved' 
                      : 'Run Constitution Check'
                    }
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        );

      case 2: // Discovery Interview
        return (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  AI-Assisted Discovery Interview
                </Typography>
                Answer a series of questions to help us understand your project requirements better. 
                This information will be used to generate a more accurate specification.
              </Alert>
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              {discoveryInterviewCompleted ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Discovery Interview Completed!</Typography>
                  <Typography variant="body2">
                    Your responses will be included in the specification generation. 
                    You can proceed to the next step.
                  </Typography>
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Please complete the discovery interview before proceeding. This helps us create 
                    a specification that accurately reflects your needs.
                  </Typography>
                </Alert>
              )}
              
              <Paper sx={{ p: 0, minHeight: '600px' }}>
                {projectIdForInterview ? (
                  <DiscoveryInterviewChat
                    projectId={projectIdForInterview}
                    initialIdea={formData.description}
                    onComplete={() => {
                      setDiscoveryInterviewCompleted(true);
                      showSuccess('Discovery interview completed successfully!');
                    }}
                  />
                ) : (
                  <Box sx={{ p: 3 }}>
                    <Alert severity="warning">
                      <Typography variant="subtitle2">Project Not Created Yet</Typography>
                      <Typography variant="body2">
                        The discovery interview will be available after the project is created. 
                        For now, you can skip this step and complete it later, or go back and 
                        create a draft project first.
                      </Typography>
                    </Alert>
                    
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Tip:</strong> If you want to use the discovery interview feature, 
                        you'll need to save a draft project first from the Project Configuration step.
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        );

      case 3: // Feature Specification
        return (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                Generate a complete feature specification using spec-kit. This will create user stories, 
                functional requirements, and success criteria.
              </Alert>
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Feature Specification
                </Typography>
                
                {formData.sddState.specification.status === PhaseStatus.COMPLETED ? (
                  <Box>
                    <Alert severity="success" sx={{ mb: 2 }}>
                      Specification completed successfully!
                    </Alert>
                    
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>View Generated Specification</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="subtitle2" gutterBottom>User Stories:</Typography>
                        <List dense>
                          {formData.sddState.specification.userStories.map(story => (
                            <ListItem key={story.id}>
                              <ListItemText 
                                primary={story.title}
                                secondary={story.description}
                              />
                            </ListItem>
                          ))}
                        </List>
                        
                        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                          Success Criteria:
                        </Typography>
                        <List dense>
                          {formData.sddState.specification.successCriteria.map(criteria => (
                            <ListItem key={criteria.id}>
                              <ListItemText 
                                primary={criteria.metric}
                                secondary={criteria.target}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      This will run the spec-kit specify command to generate a complete specification 
                      based on your project description.
                    </Typography>
                    
                    <Button
                      variant="contained"
                      onClick={handleSpecifyFeature}
                      startIcon={<DescriptionIcon />}
                      disabled={formData.sddState.constitution.status !== PhaseStatus.APPROVED}
                    >
                      Generate Specification
                    </Button>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        );

      case 4: // Implementation Planning
        return (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                Generate implementation plan including technical architecture, API contracts, 
                and data models using spec-kit planning tools.
              </Alert>
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  <CodeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Implementation Plan
                </Typography>
                
                {formData.sddState.plan.status === PhaseStatus.COMPLETED ? (
                  <Alert severity="success">
                    Implementation plan generated successfully! 
                    Technical architecture, API contracts, and data models are ready.
                  </Alert>
                ) : (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      This will run the spec-kit plan command to generate:
                    </Typography>
                    <List dense>
                      <ListItem>• Technical architecture and technology choices</ListItem>
                      <ListItem>• API contracts and data models</ListItem>
                      <ListItem>• Security and performance requirements</ListItem>
                      <ListItem>• Integration patterns and dependencies</ListItem>
                    </List>
                    
                    <Button
                      variant="contained"
                      onClick={handleGeneratePlan}
                      startIcon={<CodeIcon />}
                      disabled={formData.sddState.specification.status !== PhaseStatus.COMPLETED}
                    >
                      Generate Implementation Plan
                    </Button>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        );

      case 5: // Task Breakdown
        return (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                Break down the implementation into specific development tasks with estimates and dependencies.
              </Alert>
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  <TaskIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Development Tasks
                </Typography>
                
                {formData.sddState.tasks.status === PhaseStatus.COMPLETED ? (
                  <Alert severity="success">
                    Task breakdown completed! Development tasks are ready for assignment and implementation.
                  </Alert>
                ) : (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      This will generate:
                    </Typography>
                    <List dense>
                      <ListItem>• Detailed task breakdown by component</ListItem>
                      <ListItem>• Time estimates and complexity analysis</ListItem>
                      <ListItem>• Task dependencies and critical path</ListItem>
                      <ListItem>• Team member skill requirements</ListItem>
                    </List>
                    
                    <Button
                      variant="contained"
                      onClick={handleGenerateTasks}
                      startIcon={<TaskIcon />}
                      disabled={formData.sddState.plan.status !== PhaseStatus.COMPLETED}
                    >
                      Generate Task Breakdown
                    </Button>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        );

      case 6: // Review & Create
        return (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Alert 
                severity={formData.sddState.readyForImplementation ? "success" : "warning"} 
                sx={{ mb: 3 }}
              >
                {formData.sddState.readyForImplementation 
                  ? "Project is ready for implementation! All SDD phases completed successfully."
                  : "Complete all SDD phases before creating the project."
                }
              </Alert>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  <ProjectIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Project Summary
                </Typography>
                <Typography><strong>Name:</strong> {formData.name}</Typography>
                <Typography><strong>Client:</strong> {assignableUsers?.clients.find(c => c.id.toString() === formData.clientId)?.name}</Typography>
                <Typography><strong>Type:</strong> {formData.projectType}</Typography>
                <Typography><strong>Priority:</strong> {formData.priority}</Typography>
                <Typography><strong>SDD Workflow:</strong> {formData.enableSDDWorkflow ? 'Enabled' : 'Disabled'}</Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  <CheckCircleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  SDD Phase Status
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Constitution" />
                    <Chip 
                      label={formData.sddState.constitution.status}
                      color={formData.sddState.constitution.status === PhaseStatus.APPROVED ? 'success' : 'default'}
                      size="small"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Specification" />
                    <Chip 
                      label={formData.sddState.specification.status}
                      color={formData.sddState.specification.status === PhaseStatus.COMPLETED ? 'success' : 'default'}
                      size="small"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Planning" />
                    <Chip 
                      label={formData.sddState.plan.status}
                      color={formData.sddState.plan.status === PhaseStatus.COMPLETED ? 'success' : 'default'}
                      size="small"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Tasks" />
                    <Chip 
                      label={formData.sddState.tasks.status}
                      color={formData.sddState.tasks.status === PhaseStatus.COMPLETED ? 'success' : 'default'}
                      size="small"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  if (loadingData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Loading Project Creation Data...</Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard/admin/projects')}
          sx={{ mr: 2 }}
        >
          Back to Projects
        </Button>
        <Typography variant="h4" component="h1">
          Create New Project (SDD Workflow)
        </Typography>
      </Box>

      {/* Stepper */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stepper activeStep={activeStep} orientation="horizontal">
            {sddSteps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* Form Content */}
      <Card>
        <CardContent sx={{ p: 4 }}>
          {renderStepContent(activeStep)}
        </CardContent>
        
        <Divider />
        
        <Box sx={{ p: 3, display: 'flex', gap: 2, justifyContent: 'space-between' }}>
          <Button
            onClick={handleBack}
            disabled={activeStep === 0}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* Show Save Draft button on Project Configuration step */}
            {activeStep === 0 && !projectIdForInterview && (
              <Button
                variant="outlined"
                onClick={handleSaveDraft}
                startIcon={<SaveIcon />}
                disabled={!formData.name}
              >
                Save Draft
              </Button>
            )}
            
            {activeStep === sddSteps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!formData.sddState.readyForImplementation}
                startIcon={<SaveIcon />}
              >
                Create Project
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!validateCurrentStep()}
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default CreateProjectPageEnhanced;